import React, { useRef, useMemo, ChangeEvent } from 'react';
import { useTheme } from '@mui/material';
import DSBox from '../../../design-system/DSBox';
import DSTooltip from '../../../design-system/DSTooltip';
import { DSTypography } from '../../../design-system/Typography';
import DSHelpOutline from '../../../design-system/DSHelpOutline';
import DSIconButton from '../../../design-system/DSIconButton';
import KlydoChimeInputEmpty from './KlydoChimeInputEmpty';
import KlydoChimeInputLoading from './KlydoChimeInputLoading';
import KlydoChimeInputUploaded from './KlydoChimeInputUploaded';
import { COLLECTION_FIELDS_SPEC } from '../../../consts/specs';
import useInputsDragAndDrop, {
  createSyntheticFileEvent,
} from '../hooks/useInputsDragAndDrop';
import { FILE_VALIDATION } from '../../../consts/dragAndDropInputs';

type KlydoChimeInputProps = {
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: () => void;
  chimeUrl?: string;
  chimeName?: string;
  isLoading: boolean;
  isPlaying?: boolean;
  onPlayPause?: () => void;
  label?: string;
  tooltip?: string;
  // Overrides the shared audio cap for inputs with a tighter limit (strike tone).
  maxDurationSeconds?: number;
};

const KlydoChimeInput = ({
  onFileChange,
  onRemoveFile,
  chimeUrl,
  chimeName,
  isLoading,
  isPlaying = false,
  onPlayPause,
  label = 'Upload Special chimes (optional)',
  tooltip = COLLECTION_FIELDS_SPEC,
  maxDurationSeconds,
}: KlydoChimeInputProps) => {
  const { spacing, palette } = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileAccepted = (file: File) => {
    onFileChange(createSyntheticFileEvent(file));
  };

  const validation = useMemo(
    () =>
      maxDurationSeconds
        ? { ...FILE_VALIDATION.audio, maxDurationSeconds }
        : FILE_VALIDATION.audio,
    [maxDurationSeconds],
  );

  const { isDragging, dragHandlers } = useInputsDragAndDrop({
    onFileAccepted: handleFileAccepted,
    validation,
    enabled: !chimeUrl && !isLoading,
  });

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    onFileChange(event);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const renderChimeInputState = () => {
    if (isLoading) {
      return <KlydoChimeInputLoading />;
    }

    if (chimeUrl) {
      return (
        <KlydoChimeInputUploaded
          chimeName={chimeName}
          onRemoveFile={onRemoveFile}
          isPlaying={isPlaying}
          onPlayPause={onPlayPause}
        />
      );
    }

    return (
      <KlydoChimeInputEmpty
        onFileChange={handleFileChange}
        fileInputRef={fileInputRef}
      />
    );
  };

  return (
    <DSBox
      sx={{ display: 'flex', flexDirection: 'column', gap: spacing(1) }}
      {...dragHandlers}
    >
      <DSBox
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <DSTypography variant="label">{label}</DSTypography>
        <DSTooltip title={tooltip}>
          <DSIconButton
            sx={{
              height: spacing(1),
              '&:hover': {
                backgroundColor: 'transparent',
                color: palette.text.primary,
              },
              color: palette.text.secondary,
              transition: 'color 0.3s ease',
            }}
          >
            <DSHelpOutline />
          </DSIconButton>
        </DSTooltip>
      </DSBox>
      <DSBox
        sx={{
          border: isDragging
            ? `2px dashed ${palette.primary.main}`
            : `2px solid transparent`,
          borderRadius: '28px',
          backgroundColor: isDragging ? palette.white[10] : 'transparent',
          transition: 'all 0.2s ease',
          padding: isDragging ? spacing(0.5) : 0,
        }}
      >
        {renderChimeInputState()}
      </DSBox>
    </DSBox>
  );
};

export default KlydoChimeInput;
